
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// realisations
import { bids } from '@/core/realisations/bids.realisation'
import { carriers } from '@/core/realisations/carriers.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'
import { security } from '@/core/realisations/security.realisation'
import { cities } from '@/core/realisations/cities.realisation'
import { contracts } from '@/core/realisations/contracts.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CarrierT } from '@/core/types/carriers.types'
import { DispatcherCourierT } from '@/core/types/couriers.types'
import {
    DoDriverSearchData,
    DoDriverSearchParamsT,
    SecurityGetCheckupStatusResponseT,
} from '@/core/types/security.types'
import { CityT } from '@/core/types/cities.types'

// validation
import useVuelidate from '@vuelidate/core'
import { DoDriverSearchValidation } from '@/modules/BidsModule/validations/AddBid.validation'
import { required, requiredIf, integer } from '@vuelidate/validators'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useFormData } from '@/common/composable/useFormData'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheSelect,
        TheInput,
        FileReader,
        Checkbox,
    },
    emits: ['cancel', 'added'],
    setup(_, { emit }) {
        const notifications = useNotifications()
        const { getFormData } = useFormData()

        const state = reactive({
            is_loading: false,
            //
            carrierId: null as number | null,
            loadAddress: null as string | null,
            dateOfLoad: null as string | null,
            senderPerson: null as string | null,
            unloadAddress: null as string | null,
            dateOfUnload: null as string | null,
            receiverPerson: null as string | null,
            natureOfCargo: null as string | null,
            insurance: null as string | null,
            bodyType: null as string | null,
            temperatureRegime: null as string | null,
            costOfTransportation: null as string | null,
            driver_id: null as number | null,
            identifyCard: null as string | null,
            identifyCardDate: null as string | null,
            licence: null as string | null,
            licenceDate: null as string | null,
            brand: null as string | null,
            trailerLicenseNumber: null as string | null,
            type: null as string | null,
            //
            technicalPassport: null as string | null,
            iin: null as string | null,
            full_name: null as string | null,
            vehicleOwnerFullName: null as string | null,
            number: null as string | null,
            phone_number: null as string | null,
            carTechnicalPassportFiles: [] as File[],
            cityIds: [null, null] as (number | null)[],
            //
            isTripartite: false,
            tripartiteNumber: null as string | null,
            tripartiteDate: null as string | null,
            //
            isNds: 0,
        })

        const sb_state_iin = reactive({
            is_checked: false,
            is_rejected: null as boolean | null,
            is_loading: false,
            reject_comment: null as string | null,
            is_request_completed: false,
            //
            is_success: false,
        })

        const sb_state_car = reactive({
            is_checked: false,
            is_rejected: null as boolean | null,
            is_loading: false,
            reject_comment: null as string | null,
            is_request_completed: false,
            //
            is_success: false,
        })

        const driver_state = reactive({
            is_loading: false,
            is_not_found: false,
        })

        const contract_state = reactive({
            is_loading: false,
        })

        const AddBidValidation = {
            carrierId: { required, integer },
            loadAddress: { required },
            dateOfLoad: { required },
            senderPerson: { required },
            unloadAddress: { required },
            dateOfUnload: { required },
            receiverPerson: { required },
            natureOfCargo: { required },
            insurance: { required },
            bodyType: { required },
            temperatureRegime: { required },
            costOfTransportation: { required },
            identifyCard: { required },
            identifyCardDate: { required },
            //
            iin: { required },
            full_name: { required },
            vehicleOwnerFullName: { required },
            number: { required },
            phone_number: { required },
            carTechnicalPassportFiles: { required },
            //
            cityIds: {
                $each: {
                    required,
                },
            },
            //
            tripartiteNumber: { required: requiredIf(() => state.isTripartite) },
            tripartiteDate: { required: requiredIf(() => state.isTripartite) },
        }

        // @ts-ignore
        const state_form$ = useVuelidate(AddBidValidation, state)
        const iin_form$ = useVuelidate(DoDriverSearchValidation, state)

        const data_state = reactive({
            carriers: new DataState<CarrierT>({}),
            drivers: new DataState<DispatcherCourierT>({}),
            cities: new DataState<CityT>({}),
        })

        const termAndFormOfPayment = 'Согласно договору'

        const loadContract = async (carrier_bin: string) => {
            try {
                contract_state.is_loading = true

                const response = await contracts.getContractByBin(carrier_bin)

                state.isTripartite = true
                state.tripartiteNumber = response.data.number
                state.tripartiteDate = response.data.date
            } catch (error) {
                console.error(error)
            } finally {
                contract_state.is_loading = false
            }
        }

        // Перевозчики

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true
                const response = await carriers.getList({ page, search, statusId: 2, isExtended: 0 })
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({ page, statusId: 2, isExtended: 0 })
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        // Курьеры

        const loadDrivers = async () => {
            if (!state.carrierId) return

            try {
                data_state.drivers.is_loading = true

                const response = await couriers.getList({
                    page: 1,
                    companyId: state.carrierId,
                    statusIds: [2],
                    limit: 9999
                })

                data_state.drivers.list = response.data

                data_state.drivers.is_failed = false
                data_state.drivers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.drivers.failure.catchError(error)

                if (!data_state.drivers.failure.error_state.is_cancel) {
                    data_state.drivers.is_failed = true
                }
            } finally {
                if (!data_state.drivers.failure.error_state.is_cancel) {
                    data_state.drivers.is_loading = false
                }
            }
        }

        //

        const resetSbStateIIN = () => {
            sb_state_iin.is_checked = false
            sb_state_iin.is_rejected = null
            sb_state_iin.reject_comment = null
            sb_state_iin.is_request_completed = false
            sb_state_iin.is_success = false
        }

        const resetSbStateCar = () => {
            sb_state_car.is_checked = false
            sb_state_car.is_rejected = null
            sb_state_car.reject_comment = null
            sb_state_car.is_request_completed = false
            sb_state_car.is_success = false
        }

        const isValidIIN = computed(() => {
            return !sb_state_iin.is_loading && state.iin && state.iin.length >= 12
        })

        const checkSbStatusIIN = async () => {
            if (!isValidIIN.value) return
            resetSbStateIIN()

            try {
                sb_state_iin.is_loading = true

                const PERSON_CHECKUP_TYPE = 2

                const response: any = await security.getCheckupStatus({
                    identifier: state.iin as string,
                    type: PERSON_CHECKUP_TYPE,
                })

                if (!response.success) {
                    notifications.notify({
                        title: response.message || 'При проверке произошла ошибка',
                        type: 'danger',
                    })
                }
                sb_state_iin.is_checked = true

                if (response.data.isAccepted === false) {
                    sb_state_iin.is_rejected = true
                    sb_state_iin.reject_comment = response.data.comment
                } else if (response.data.isAccepted === true) {
                    sb_state_iin.is_success = true
                    sb_state_iin.is_rejected = false
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При проверке статуса СБ произошла ошибка',
                })

                sb_state_iin.is_checked = false
            } finally {
                sb_state_iin.is_loading = false
                sb_state_iin.is_request_completed = true
            }
        }

        const checkSbStatusCar = async () => {
            if (sb_state_car.is_loading || !state.number) return
            resetSbStateCar()

            try {
                sb_state_car.is_loading = true

                const AUTO_CHECKUP_TYPE = 3

                const response: SecurityGetCheckupStatusResponseT = await security.getCheckupStatus(
                    {
                        identifier: state.number as string,
                        type: AUTO_CHECKUP_TYPE,
                    }
                )

                sb_state_car.is_checked = true

                if (!response.data.isAccepted) {
                    sb_state_car.is_rejected = true
                    sb_state_car.reject_comment = response.data.comment

                    notifications.notify({
                        title: response.message || 'При проверке произошла ошибка',
                        type: 'danger',
                    })
                } else {
                    sb_state_car.is_success = true
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При проверке статуса СБ произошла ошибка',
                })

                sb_state_car.is_checked = false
            } finally {
                sb_state_car.is_loading = false
                sb_state_car.is_request_completed = true
            }
        }

        const doChecks = async () => {
            await checkSbStatusIIN()
            await checkSbStatusCar()
        }

        const getTripartiteDate = () => {
            if (!state.tripartiteDate) return null

            return state.tripartiteDate.split('-').reverse().join('.')
        }

        const getPayload = () => {
            let driver = {
                id: state.driver_id,
                iin: state.iin,
                fullName: state.full_name,
                vehicleOwnerFullName: state.vehicleOwnerFullName,
                phone: state.phone_number,
                //
                identifyCard: state.identifyCard,
                identifyCardDate: state.identifyCardDate,
                licence: state.licence,
                licenceDate: state.licenceDate,
            }

            let car = {
                number: state.number,
                technicalPassport: state.technicalPassport,
                //
                brand: state.brand,
                trailerLicenseNumber: state.trailerLicenseNumber,
                type: state.type,
            }

            const payload: any = {
                carrierId: state.carrierId,
                loadAddress: state.loadAddress,
                dateOfLoad: state.dateOfLoad,
                senderPerson: state.senderPerson,
                unloadAddress: state.unloadAddress,
                dateOfUnload: state.dateOfUnload,
                receiverPerson: state.receiverPerson,
                natureOfCargo: state.natureOfCargo,
                insurance: state.insurance,
                bodyType: state.bodyType,
                temperatureRegime: state.temperatureRegime,
                costOfTransportation: state.costOfTransportation,
                driver,
                car,
                carTechnicalPassportFiles: state.carTechnicalPassportFiles,
                cityIds: state.cityIds,
                isNds: state.isNds,
                // hardcode
                senderCompanyId: 91,
                receiverCompanyId: 91,
                termAndFormOfPayment,
            }

            if (state.isTripartite) {
                payload.tripartiteNumber = state.tripartiteNumber
                payload.tripartiteDate = getTripartiteDate()
            }

            return payload
        }

        const addBid = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            await doChecks()
            if (!sb_state_iin.is_success || !sb_state_car.is_success) return

            try {
                state.is_loading = true

                let payload = getPayload()

                if (!payload) return

                await bids.createBid(getFormData(payload))
                notifications.notify({ title: 'Заявка успешно создана!', type: 'success' })
                emit('added')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При создании заявки произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const resetForm = () => {
            state.carrierId = null
            state.loadAddress = null
            state.dateOfLoad = null
            state.senderPerson = null
            state.unloadAddress = null
            state.dateOfUnload = null
            state.receiverPerson = null
            state.natureOfCargo = null
            state.insurance = null
            state.bodyType = null
            state.temperatureRegime = null
            state.costOfTransportation = null
            //
            state.driver_id = null
            state.identifyCard = null
            state.identifyCardDate = null
            state.licence = null
            state.licenceDate = null
            state.technicalPassport = null
            //
            state.brand = null
            state.trailerLicenseNumber = null
            state.type = null
            //
            state.iin = null
            state.full_name = null
            state.vehicleOwnerFullName = null
            state.number = null
            state.phone_number = null
            //
            state.isTripartite = false
            state.tripartiteNumber = null
            state.tripartiteDate = null
            //
            state.carTechnicalPassportFiles = []
            state.cityIds = [null, null]

            state.isNds = 0

            state_form$.value.$reset()
        }

        const onDriverSelect = (driver_id: number) => {
            let driver_selected = data_state.drivers.list.find((driver) => driver.id === driver_id)

            if (driver_selected) {
                state.iin = driver_selected.iin
                state.full_name = driver_selected.fullName
                state.number = driver_selected.car ? driver_selected.car.number : null
                state.phone_number = driver_selected.phoneNumber
            }
        }

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search, 9999)
                data_state.cities.list = data.data

                data_state.cities.pagination.total_pages = data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const citiesLength = computed(() => state.cityIds.length)

        const addCity = (index: number) => state.cityIds.splice(index, 0, null)
        const deleteCity = (index: number) => state.cityIds.splice(index, 1)

        const getCityPlaceholder = (idx: number) => {
            if (idx === 0) {
                return 'Город отправки'
            } else if (idx === citiesLength.value - 1) {
                return 'Город прибытия'
            } else {
                return 'Город транзит'
            }
        }

        const isAddCity = (idx: number) => {
            return idx === citiesLength.value - 1
        }

        const fullFillDriverInfo = (data: DoDriverSearchData) => {
            state.full_name = data.fullName
            state.phone_number = data.phone
            state.brand = data.carBrand
            state.type = data.carType
            state.trailerLicenseNumber = data.trailerLicenseNumber
            state.identifyCard = data.identifyCard
            state.identifyCardDate = data.identifyCardDate
            state.licence = data.license
            state.licenceDate = data.licenseDate
            state.number = data.carNumber
        }

        const doDriverSearch = async () => {
            iin_form$.value.$touch()
            if (iin_form$.value.$invalid) return

            try {
                driver_state.is_loading = true
                driver_state.is_not_found = false

                const payload = {
                    iin: state.iin,
                }

                const { data } = await security.doDriverSearch(payload as DoDriverSearchParamsT)

                if (data) {
                    fullFillDriverInfo(data)
                } else {
                    driver_state.is_not_found = true
                }
            } catch (error) {
                driver_state.is_not_found = true
                notifications.notifyError({
                    error,
                    title: 'При проверке произошла ошибка',
                })
            } finally {
                driver_state.is_loading = false
            }
        }

        const onLoadContract = (carrier_id: number) => {
            const carrier = data_state.carriers.list.find((c) => c.id === carrier_id)

            if (!carrier || [2, 4].includes(carrier.status_id)) return
            const { bin, vehicleOwner } = carrier
            if (!vehicleOwner || !bin) return

            loadContract(bin)
        }

        const onCarrierChange = (carrier_id: number) => {
            loadDrivers()
            onLoadContract(carrier_id)

            if (carrier_id) {
                const carrier = data_state.carriers.list.find((c) => c.id === carrier_id)

                if (carrier) {
                    state.isNds = Number(carrier.is_nds)
                }
            } else {
                state.isNds = 0
            }
        }

        const onIsTripartiteChange = () => {
            state.tripartiteNumber = null
            state.tripartiteDate = null
        }

        //

        const emitCancel = () => {
            emit('cancel')
        }

        onBeforeMount(() => {
            loadCarriers(1)
            loadCities(1)
        })

        // computed IIN

        const isNotFoundIIN = computed(
            () => sb_state_iin.is_rejected === null && sb_state_iin.is_request_completed
        )
        const isRejectedIIN = computed(
            () => sb_state_iin.is_rejected && sb_state_iin.is_request_completed
        )
        const isNotCheckedIIN = computed(
            () => !sb_state_iin.is_checked && sb_state_iin.is_request_completed
        )

        // computed Car

        const isRejectedCar = computed(
            () => sb_state_car.is_rejected && sb_state_car.is_request_completed
        )
        const isNotCheckedCar = computed(
            () => !sb_state_car.is_checked && sb_state_car.is_request_completed
        )

        //

        const isDelete = (index: number) =>
            index > 0 && index < state.cityIds.length - 1 && state.cityIds.length > 2

        //

        return {
            state,
            driver_state,
            sb_state_iin,
            sb_state_car,
            state_form$,
            iin_form$,
            data_state,
            emitCancel,
            doDriverSearch,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            onCarrierChange,
            onIsTripartiteChange,
            deleteCity,
            //
            loadDrivers,
            //
            loadCities,
            addCity,
            getCityPlaceholder,
            isAddCity,
            //
            addBid,
            resetForm,
            onDriverSelect,
            //
            termAndFormOfPayment,
            // iin computed
            isRejectedIIN,
            isNotCheckedIIN,
            isNotFoundIIN,
            // car computed
            isRejectedCar,
            isNotCheckedCar,
            //
            citiesLength,
            isDelete,
        }
    },
})
